import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useVehicleStore } from '../../stores/vehicleStore';
import { Vehicle } from '../../types';

const AddVehicleScreen: React.FC = () => {
  const navigation = useNavigation();
  const { addVehicle, loading } = useVehicleStore();

  const [formData, setFormData] = useState({
    licensePlate: '',
    makeModel: '',
    year: '',
    vinNumber: '',
    carStatus: 'available' as Vehicle['carStatus'],
    dailyRent: '',
    insuranceExpiry: '',
    registrationExpiry: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showStatusModal, setShowStatusModal] = useState(false);

  const statusOptions = [
    { label: 'Available', value: 'available' },
    { label: 'In Workshop', value: 'in_workshop' },
    { label: 'Out of Service', value: 'out_of_service' },
    { label: 'Stop', value: 'stop' },
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.licensePlate.trim()) {
      newErrors.licensePlate = 'License plate is required';
    } else if (formData.licensePlate.trim().length < 2) {
      newErrors.licensePlate = 'License plate must be at least 2 characters';
    }

    if (!formData.makeModel.trim()) {
      newErrors.makeModel = 'Make/Model is required';
    }

    if (!formData.year.trim()) {
      newErrors.year = 'Year is required';
    } else {
      const yearNum = parseInt(formData.year);
      if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear() + 1) {
        newErrors.year = 'Please enter a valid year';
      }
    }

    // VIN validation (should be exactly 17 characters if provided)
    if (formData.vinNumber.trim() && formData.vinNumber.trim().length !== 17) {
      newErrors.vinNumber = 'VIN must be exactly 17 characters';
    }

    // Daily rent validation
    if (formData.dailyRent) {
      const rentNum = Number(formData.dailyRent);
      if (isNaN(rentNum)) {
        newErrors.dailyRent = 'Daily rent must be a number';
      } else if (rentNum < 0) {
        newErrors.dailyRent = 'Daily rent cannot be negative';
      } else if (rentNum > 10000) {
        newErrors.dailyRent = 'Daily rent seems unusually high (max 10,000 AED)';
      }
    }

    // Date validation for insurance expiry
    if (formData.insuranceExpiry) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.insuranceExpiry)) {
        newErrors.insuranceExpiry = 'Date must be in YYYY-MM-DD format';
      } else {
        const date = new Date(formData.insuranceExpiry);
        if (isNaN(date.getTime())) {
          newErrors.insuranceExpiry = 'Please enter a valid date';
        }
      }
    }

    // Date validation for registration expiry
    if (formData.registrationExpiry) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.registrationExpiry)) {
        newErrors.registrationExpiry = 'Date must be in YYYY-MM-DD format';
      } else {
        const date = new Date(formData.registrationExpiry);
        if (isNaN(date.getTime())) {
          newErrors.registrationExpiry = 'Please enter a valid date';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Alert.alert(
        'Validation Error',
        'Please correct the errors in the form before submitting.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      const vehicleData: Omit<Vehicle, 'id' | 'created_at'> = {
        licensePlate: formData.licensePlate.trim().toUpperCase(),
        makeModel: formData.makeModel.trim(),
        year: parseInt(formData.year),
        vinNumber: formData.vinNumber.trim() || undefined,
        carStatus: formData.carStatus,
        dailyRent: formData.dailyRent ? Number(formData.dailyRent) : undefined,
        insuranceExpiry: formData.insuranceExpiry || undefined,
        registrationExpiry: formData.registrationExpiry || undefined,
        notes: formData.notes.trim() || undefined,
      };

      await addVehicle(vehicleData);
      
      Alert.alert(
        'Success',
        `Vehicle ${vehicleData.licensePlate} has been added successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              // Navigate to Vehicles list
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                // If can't go back, navigate to Vehicles route
                (navigation as any).navigate?.('Vehicles');
              }
            },
          },
        ]
      );
    } catch (error: any) {
      Alert.alert(
        'Error',
        `Failed to add vehicle: ${error.message || 'Unknown error'}`,
        [{ text: 'OK' }]
      );
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container} 
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.title}>Add New Vehicle</Text>
          <Text style={styles.subtitle}>Enter vehicle details</Text>
        </View>

        <View style={styles.formContainer}>
          {/* License Plate */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>License Plate *</Text>
            <TextInput
              style={[styles.input, errors.licensePlate && styles.inputError]}
              value={formData.licensePlate}
              onChangeText={(value) => updateFormData('licensePlate', value)}
              placeholder="e.g., ABC123"
              autoCapitalize="characters"
              maxLength={20}
            />
            {errors.licensePlate && (
              <Text style={styles.errorText}>{errors.licensePlate}</Text>
            )}
          </View>

          {/* Make/Model */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Make/Model *</Text>
            <TextInput
              style={[styles.input, errors.makeModel && styles.inputError]}
              value={formData.makeModel}
              onChangeText={(value) => updateFormData('makeModel', value)}
              placeholder="e.g., Toyota Camry"
              maxLength={100}
            />
            {errors.makeModel && (
              <Text style={styles.errorText}>{errors.makeModel}</Text>
            )}
          </View>

          {/* Year */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Year *</Text>
            <TextInput
              style={[styles.input, errors.year && styles.inputError]}
              value={formData.year}
              onChangeText={(value) => updateFormData('year', value)}
              placeholder="e.g., 2020"
              keyboardType="numeric"
              maxLength={4}
            />
            {errors.year && (
              <Text style={styles.errorText}>{errors.year}</Text>
            )}
          </View>

          {/* VIN Number */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>VIN Number</Text>
            <TextInput
              style={[styles.input, errors.vinNumber && styles.inputError]}
              value={formData.vinNumber}
              onChangeText={(value) => updateFormData('vinNumber', value)}
              placeholder="17-character Vehicle Identification Number"
              autoCapitalize="characters"
              maxLength={17}
            />
            {errors.vinNumber && (
              <Text style={styles.errorText}>{errors.vinNumber}</Text>
            )}
          </View>

          {/* Car Status */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Status</Text>
            <TouchableOpacity
              style={styles.dropdown}
              onPress={() => setShowStatusModal(true)}
            >
              <Text style={styles.dropdownText}>
                {statusOptions.find(option => option.value === formData.carStatus)?.label || 'Select Status'}
              </Text>
              <Text style={styles.dropdownArrow}>▼</Text>
            </TouchableOpacity>
          </View>

          {/* Daily Company Share */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Daily Company Share (AED)</Text>
            <TextInput
              style={[styles.input, errors.dailyRent && styles.inputError]}
              value={formData.dailyRent}
              onChangeText={(value) => updateFormData('dailyRent', value)}
              placeholder="e.g., 150"
              keyboardType="numeric"
            />
            {errors.dailyRent && (
              <Text style={styles.errorText}>{errors.dailyRent}</Text>
            )}
          </View>

          {/* Insurance Expiry */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Insurance Expiry</Text>
            <TextInput
              style={[styles.input, errors.insuranceExpiry && styles.inputError]}
              value={formData.insuranceExpiry}
              onChangeText={(value) => updateFormData('insuranceExpiry', value)}
              placeholder="YYYY-MM-DD (e.g., 2025-12-31)"
            />
            {errors.insuranceExpiry && (
              <Text style={styles.errorText}>{errors.insuranceExpiry}</Text>
            )}
          </View>

          {/* Registration Expiry */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Registration Expiry</Text>
            <TextInput
              style={[styles.input, errors.registrationExpiry && styles.inputError]}
              value={formData.registrationExpiry}
              onChangeText={(value) => updateFormData('registrationExpiry', value)}
              placeholder="YYYY-MM-DD (e.g., 2025-12-31)"
            />
            {errors.registrationExpiry && (
              <Text style={styles.errorText}>{errors.registrationExpiry}</Text>
            )}
          </View>

          {/* Notes */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(value) => updateFormData('notes', value)}
              placeholder="Additional notes about the vehicle"
              multiline
              numberOfLines={3}
              maxLength={500}
            />
          </View>
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => navigation.goBack()}
            disabled={loading}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            <Text style={styles.submitButtonText}>
              {loading ? 'Adding...' : 'Add Vehicle'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Status Selection Modal */}
      <Modal
        visible={showStatusModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStatusModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Vehicle Status</Text>
            {statusOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.modalOption,
                  formData.carStatus === option.value && styles.modalOptionSelected
                ]}
                onPress={() => {
                  updateFormData('carStatus', option.value);
                  setShowStatusModal(false);
                }}
              >
                <Text
                  style={[
                    styles.modalOptionText,
                    formData.carStatus === option.value && styles.modalOptionTextSelected
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => setShowStatusModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollView: {
    flex: 1,
  },
  header: {
    backgroundColor: '#fff',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
  },
  formContainer: {
    backgroundColor: '#fff',
    margin: 16,
    borderRadius: 12,
    padding: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  inputError: {
    borderColor: '#ff4444',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  dropdown: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: 16,
    color: '#333',
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#666',
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  submitButton: {
    flex: 1,
    backgroundColor: '#007bff',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    minWidth: 280,
    maxWidth: '80%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  modalOptionSelected: {
    backgroundColor: '#007bff20',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
  },
  modalOptionTextSelected: {
    color: '#007bff',
    fontWeight: '600',
  },
  modalCancelButton: {
    marginTop: 16,
    padding: 12,
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '600',
  },
});

export default AddVehicleScreen;